<template>
  <div class="competition-about">
    <p>
      At the International Adult Ballet Festival, we recognize that adult ballet dancers come to the
      stage with a wide range of experiences, aspirations, and motivations. The addition of a
      competition element is not about creating a hierarchy&mdash;it&apos;s about creating opportunity.
    </p>

    <p>
      For some, the chance to be seen by a panel of judges and receive constructive feedback is deeply
      meaningful. For others, it&apos;s the joy of standing alongside peers, sharing a stage with dancers
      who understand what it means to start or return to ballet as an adult. Some competitors grew up
      watching youth competitions and longed to participate. Others are looking for a personal
      challenge&mdash;a milestone that pushes them to their full potential.
    </p>

    <p>Whatever the reason, the IABF competition is designed to be:</p>
    <ul>
      <li>
        <strong>Supportive:</strong> A space where growth is celebrated and bravery is honored.
      </li>
      <li>
        <strong>Constructive:</strong> With thoughtful feedback from judges to guide and inspire future
        development.
      </li>
      <li>
        <strong>Inclusive:</strong> With divisions that reflect the unique paths adult dancers walk,
        whether they&apos;re dancing on flat, en pointe, or performing for the very first time.
      </li>
      <li>
        <strong>Gracious:</strong> All participants are expected to uphold the values of kindness,
        encouragement, and sportsmanship. Disappointment is a natural part of any competitive
        experience, but so is resilience, joy, and mutual respect.
      </li>
    </ul>

    <p>
      This is not just a competition. It&apos;s a celebration of adult dancers who dare to show up, shine
      brightly, and inspire each other. We believe that growth is the real win, and the courage to
      perform is the highest achievement of all.
    </p>

    <h4>Competition Entry</h4>
    <p>Each dancer may enter one solo into the competition for feedback and judging.</p>
    <p>
      Dancers are warmly invited to perform additional solos as non-competing pieces&mdash;perfect for
      exploring artistry, showcasing progress, or simply enjoying the stage without pressure.
    </p>

    <h4>Why Only One Competition Entry?</h4>
    <p>
      To keep the performance flowing smoothly and maintain the focus on artistry, the competition is
      structured to include only one judged piece per dancer. This allows us to:
    </p>
    <ul>
      <li>
        <strong>Maintain a seamless program flow.</strong> Judges complete their feedback during
        non-competing pieces, allowing the show to run continuously without long pauses.
      </li>
      <li>
        <strong>Offer personalized feedback.</strong> Each competitor receives thoughtful,
        individualized attention.
      </li>
      <li>
        <strong>Celebrate all performances equally.</strong> Non-competing pieces are not identified
        during the show, so every dancer shares the stage on equal artistic footing.
      </li>
      <li>
        <strong>Balance challenge and celebration.</strong> By including both competitive and
        non-competitive performances, the event remains vibrant, inclusive, and encouraging.
      </li>
    </ul>

    <h4>Competition Participation Requirements</h4>
    <p>
      <strong>Level Advancement:</strong> If you have previously won a gold medal at the Beginner or
      Intermediate IABF competition level, you are asked to move up to the next level for future
      entries in that same division.
    </p>
    <p>
      <em>Example:</em> A Beginner level gold medalist in Classical Variation would move to Intermediate
      for their next Classical competition entry.
    </p>
    <p>
      However, you may still enter at the Beginner level in a different division, such as
      Contemporary/Lyrical, if that is a newer or less familiar style for you.
    </p>

    <h4>Divisions</h4>
    <p><strong>Style Categories</strong></p>
    <p>Dancers will register their solo into one of the following divisions:</p>
    <ul>
      <li>
        <strong>Classical Variation</strong> &mdash; Traditional ballet variation. (Beginner and
        Beginner Pointe levels may modify choreography as needed.)
      </li>
      <li>
        <strong>Original Ballet Choreography</strong> &mdash; A self-choreographed or newly created
        solo in a classical or neoclassical style.
      </li>
      <li>
        <strong>Contemporary/Lyrical</strong> &mdash; Expressive movement rooted in ballet technique,
        performed to contemporary music or exploring modern themes.
      </li>
    </ul>

    <p><strong>Skill Levels</strong></p>
    <p>
      Dancers will self-assess and place themselves in the following skill levels during registration.
      These levels reflect your current technical comfort and performance readiness&mdash;not simply
      years of training.
    </p>
    <ul>
      <li>Beginner</li>
      <li>Intermediate</li>
      <li>Advanced</li>
      <li>Professional</li>
    </ul>
    <p class="small-note">
      *IABF reserves the right to reassign levels at the judges&apos; discretion.
    </p>

    <h4>Awards &amp; Recognition</h4>
    <p><strong>Medal Structure</strong></p>
    <ul>
      <li>
        <strong>Gold, Silver, and Bronze</strong> awarded when a division has 5 or more competitors
      </li>
      <li>
        <strong>Gold and Silver</strong> awarded when a division has 3&ndash;4 competitors
      </li>
      <li>
        <strong>Gold only</strong> awarded when a division has 1&ndash;2 competitors
      </li>
    </ul>

    <p><strong>Judges&apos; Choice Awards</strong></p>
    <p>
      In addition to medals, Judges&apos; Choice Awards will be given to honor special qualities that
      may not be fully captured by numerical scoring. Our themed Judges&apos; Choice awards rotate each
      year and celebrate dancers for their passion and outstanding memorable performance. These are
      awarded by the judges to the dancers who most embodied each characteristic.
    </p>

    <h4>Prizes and Honors</h4>
    <ul>
      <li>All medalists will be invited to select a prize from the IABF Prize Table.</li>
    </ul>

    <p>All competition dancers will receive:</p>
    <ul>
      <li>Individual written feedback from each judge</li>
      <li>
        Scores based on:
        <ul>
          <li>Technique</li>
          <li>Artistry</li>
          <li>Musicality</li>
          <li>Expression</li>
          <li>Alignment with division standards</li>
        </ul>
      </li>
      <li>Personalized encouragement to support ongoing growth</li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'CompetitionAbout',
};
</script>

<style scoped>
.competition-about {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  line-height: 1.55;
  color: var(--iabf-ink, #1c1b1f);
}

.competition-about p {
  margin: 0 0 12px;
}

.competition-about h4 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 17px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
  margin: 22px 0 8px;
}

.competition-about ul {
  margin: 0 0 12px;
  padding-left: 1.25em;
}

.competition-about li {
  margin-bottom: 6px;
}

.competition-about li ul {
  margin-top: 6px;
  margin-bottom: 0;
}

.competition-about .small-note {
  font-size: 13px;
  color: var(--iabf-ink-muted, #5c5a63);
  font-style: italic;
  margin-top: 4px;
}

.competition-about em {
  font-style: italic;
}
</style>
